import {
  GridCardProps,
  GridCardCtaStatus,
} from 'components/logo-grid-card/LogoGridCard.types';
import { formatPhone } from 'utils/phone-number';
import { ColorKey, Text } from '@updater/ui-uds';
import {
  GridCardColumn,
  GridCardSubTitle,
  GridCardLogo,
  GridCardCTA,
  GridCardColumnButton,
} from '../logo-grid-card/LogoGridCard.styles';
import {
  InnerGrid,
  GridCardColumnLeft,
  GridCardColumnRight,
  BottomArrowSquareOut,
  CTATextLink,
} from './UtilityCard.styles';
import { LogoSuccessMessage } from '../logo-grid-card/LogoGridCard';
import { ResponsiveValue, Theme } from 'styled-system';

interface CardCTAProps {
  status: GridCardCtaStatus;
  onClick: () => void;
  href: string | undefined;
  ctaLabel: string;
}

const utilitySuccessCopy = `You're good to go! Your building set this up.`;

const CardCTA: React.FC<CardCTAProps> = ({
  status,
  onClick,
  href,
  ctaLabel,
}) => {
  if (!href) return null;
  switch (status) {
    case GridCardCtaStatus.SUCCESS:
      return <LogoSuccessMessage successCopy={utilitySuccessCopy} />;
    case GridCardCtaStatus.LINKOUT:
      return (
        <GridCardCTA>
          <CTATextLink
            href={href}
            onClick={onClick}
            data-cy="utilityCardRightButton"
            rel=""
            target="_blank"
          >
            <span>
              {ctaLabel} <BottomArrowSquareOut size={20} />
            </span>
          </CTATextLink>
        </GridCardCTA>
      );
    default:
      return (
        <GridCardCTA>
          <GridCardColumnButton
            isFluidWidth
            onClick={onClick}
            size="l"
            variant="primary"
          >
            {ctaLabel}
          </GridCardColumnButton>
        </GridCardCTA>
      );
  }
};

export const UtilityCard: React.FC<GridCardProps> = ({
  status = GridCardCtaStatus.DEFAULT,
  subtitle,
  phoneNumber,
  href = '',
  onClick = () => {},
  logo,
  ctaCopy = 'Visit website',
  span = '3',
  spanStart,
}) => {
  const showFallbackText = !subtitle && !phoneNumber && !href;
  return (
    <GridCardColumn
      data-cy="utilityCard"
      borderColor={
        'light' as ResponsiveValue<ColorKey, Theme> & ('dark' | 'light')
      }
      variant="squared"
      spanStart={spanStart}
      span={span}
    >
      <InnerGrid>
        <GridCardColumnLeft>
          <GridCardLogo data-cy="utilityCardLeft">{logo}</GridCardLogo>
        </GridCardColumnLeft>
        <GridCardColumnRight data-cy="utilityCardRight">
          {showFallbackText ? (
            <Text variant="m">
              We’re having trouble locating your provider. Check back soon!
            </Text>
          ) : (
            <>
              {subtitle ? (
                <GridCardSubTitle as="span" variant="mBold" color="primary">
                  {subtitle}
                </GridCardSubTitle>
              ) : null}
              {phoneNumber ? (
                <Text as="p" variant="m">
                  {formatPhone(phoneNumber)}
                </Text>
              ) : null}
              <CardCTA
                status={status}
                onClick={onClick}
                href={href}
                ctaLabel={ctaCopy}
              />
            </>
          )}
        </GridCardColumnRight>
      </InnerGrid>
    </GridCardColumn>
  );
};
